import { IconAlertTriangle, IconX } from '@tabler/icons-react'
import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react'
import { Button, type ButtonVariant } from './Button'
import { Modal } from './Overlay'
import { cx } from './primitives'

/* ------------------------------------------------------------- bildirim */

export type Tone = 'neutral' | 'success' | 'danger' | 'warning' | 'info'

/** Eski renk adlarını (çağrı yerleri) anlamlı tonlara çevirir. */
export function toneOf(color?: string): Tone {
  switch (color) {
    case 'green': case 'teal': return 'success'
    case 'red': return 'danger'
    case 'orange': case 'yellow': return 'warning'
    case 'blue': case 'brand': case 'indigo': return 'info'
    default: return 'neutral'
  }
}

interface ToastItem { id: number; tone: Tone; title?: ReactNode; message: ReactNode; autoClose: number | false }
let toasts: ToastItem[] = []
let seq = 0
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

export interface NotifyInput {
  message: ReactNode
  title?: ReactNode
  /** Eski çağrılarla uyumlu: 'green' | 'red' | 'orange' | 'blue' | 'gray' … */
  color?: string
  tone?: Tone
  autoClose?: number | false
}

/** Uygulama genelinde bildirim. `notifications.show({ color, message })` biçimi korunur. */
export const notifications = {
  show(input: NotifyInput) {
    const item: ToastItem = {
      id: ++seq, tone: input.tone ?? toneOf(input.color), title: input.title, message: input.message,
      autoClose: input.autoClose === undefined ? 5000 : input.autoClose,
    }
    toasts = [...toasts, item].slice(-5)
    emit()
    return item.id
  },
  hide(id: number) { toasts = toasts.filter((t) => t.id !== id); emit() },
  clean() { toasts = []; emit() },
}

function Toast({ t }: { t: ToastItem }) {
  useEffect(() => {
    if (!t.autoClose) return
    const h = setTimeout(() => notifications.hide(t.id), t.autoClose)
    return () => clearTimeout(h)
  }, [t])
  return (
    <div className={cx('ui-toast', `ui-toast--${t.tone}`)} role={t.tone === 'danger' ? 'alert' : 'status'}>
      <span className="ui-toast__bar" aria-hidden="true" />
      <div className="ui-toast__body">
        {t.title && <div className="ui-toast__title">{t.title}</div>}
        <div>{t.message}</div>
      </div>
      <button type="button" className="ui-icon-btn ui-icon-btn--sm" aria-label="Bildirimi kapat" onClick={() => notifications.hide(t.id)}>
        <IconX size={15} />
      </button>
    </div>
  )
}

export function Toaster() {
  const list = useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb) }, () => toasts, () => toasts)
  return (
    <div className="ui-toasts" aria-live="polite">
      {list.map((t) => <Toast key={t.id} t={t} />)}
    </div>
  )
}

/* ------------------------------------------------------------ onay penceresi */

interface ConfirmInput {
  title: ReactNode
  children?: ReactNode
  labels?: { confirm?: ReactNode; cancel?: ReactNode }
  /** Eski çağrılarla uyumlu: { color: 'red' | 'orange' } */
  confirmProps?: { color?: string }
  onConfirm?: () => void
  onCancel?: () => void
}
let pending: (ConfirmInput & { id: number }) | null = null
const cListeners = new Set<() => void>()

/** `modals.openConfirmModal({...})` biçimi korunur. */
export const modals = {
  openConfirmModal(input: ConfirmInput) {
    pending = { ...input, id: ++seq }
    cListeners.forEach((l) => l())
  },
  closeAll() { pending = null; cListeners.forEach((l) => l()) },
}

export function ConfirmHost() {
  const cur = useSyncExternalStore((cb) => { cListeners.add(cb); return () => cListeners.delete(cb) }, () => pending, () => pending)
  const close = () => { pending = null; cListeners.forEach((l) => l()) }
  if (!cur) return null
  const color = cur.confirmProps?.color
  const variant: ButtonVariant = color === 'red' ? 'danger' : color === 'orange' ? 'warning' : 'primary'
  return (
    <Modal opened onClose={() => { cur.onCancel?.(); close() }} title={cur.title} size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => { cur.onCancel?.(); close() }}>{cur.labels?.cancel ?? 'Vazgeç'}</Button>
          <Button variant={variant} onClick={() => { close(); cur.onConfirm?.() }}>{cur.labels?.confirm ?? 'Onayla'}</Button>
        </>
      }>
      <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--nx-fg-2)' }}>{cur.children}</div>
    </Modal>
  )
}

/* ------------------------------------------------------------- durumlar */

export function Alert({
  tone = 'neutral', title, icon, children, className, color,
}: { tone?: Tone | 'lime'; title?: ReactNode; icon?: ReactNode; children?: ReactNode; className?: string; color?: string }) {
  const t = color ? toneOf(color) : tone
  return (
    <div className={cx('ui-alert', `ui-alert--${t}`, className)} role={t === 'danger' ? 'alert' : undefined}>
      {icon && <span style={{ display: 'flex', marginTop: 1 }}>{icon}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <div className="ui-alert__title">{title}</div>}
        {children}
      </div>
    </div>
  )
}

export function Loader({ size = 'md', label }: { size?: 'sm' | 'md' | 'lg'; label?: string }) {
  return <span className={cx('ui-loader', size !== 'md' && `ui-loader--${size}`)} role="status" aria-label={label ?? 'Yükleniyor'} />
}

export function Dots() {
  return <span className="ui-dots" aria-hidden="true"><span /><span /><span /></span>
}

export function Progress({
  value, size = 'md', color, label, className,
}: { value: number; size?: 'sm' | 'md' | 'lg'; color?: string; label?: string; className?: string }) {
  const v = Math.max(0, Math.min(100, value))
  return (
    <div className={cx('ui-progress', size !== 'md' && `ui-progress--${size}`, className)} role="progressbar"
      aria-valuenow={Math.round(v)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <div className="ui-progress__bar" style={{ width: `${v}%`, background: color }} />
    </div>
  )
}

export function LoadingState({ label = 'Yükleniyor…' }: { label?: string }) {
  return <div className="ui-state"><Loader size="sm" label={label} /><span>{label}</span></div>
}

export function ErrorState({ message = 'Bir hata oluştu.', onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <div className="ui-state ui-state--error" role="alert">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 500 }}><IconAlertTriangle size={18} />{message}</span>
      {onRetry && <Button variant="secondary" size="sm" onClick={onRetry}>Tekrar dene</Button>}
    </div>
  )
}

export function EmptyState({
  icon, title, hint, action,
}: { icon?: ReactNode; title: ReactNode; hint?: ReactNode; action?: ReactNode }) {
  return (
    <div className="ui-empty">
      {icon && <span className="ui-empty__icon" aria-hidden="true">{icon}</span>}
      <span className="ui-empty__title">{title}</span>
      {hint && <span className="ui-empty__hint">{hint}</span>}
      {action}
    </div>
  )
}

/** Kısa süreli "Kopyalandı" durumu olan kopyalama yardımcısı. */
export function useCopy(timeout = 1500) {
  const [copied, setCopied] = useState(false)
  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text) } catch { /* izin yoksa sessiz */ }
    setCopied(true)
    setTimeout(() => setCopied(false), timeout)
  }
  return { copied, copy }
}
